import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';

import { Box, Chip, Typography } from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import FlashOnRoundedIcon from '@mui/icons-material/FlashOnRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
// import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';

import { GoogleSignIn } from '../../redux/actions/authActions';
import { ColorModeContext } from '../../App';
import LogoSVG from '../../assets/icons/LogoSVG';
import { trackSignupSuccess } from '../../utils/analytics';

import "./styles.scss";

// ── Floating orb canvas ──────────────────────────────────────────────────────
const OrbCanvas = ({ mode }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const COLORS = ['#8b5cf6', '#38bdf8', '#14b8a6', '#6366f1', '#a78bfa', '#0ea5e9'];
    const opacity = mode === 'dark' ? '30' : '1e'; // hex alpha: ~19% dark, ~12% light

    const orbs = Array.from({ length: 7 }, (_, i) => ({
      x:  (canvas.width  / 7) * i + Math.random() * (canvas.width  / 7),
      y:  Math.random() * canvas.height,
      r:  180 + Math.random() * 200,
      vx: (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 2,
      color: COLORS[i % COLORS.length],
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      orbs.forEach(o => {
        o.x += o.vx;
        o.y += o.vy;
        if (o.x < -o.r) o.x = canvas.width  + o.r;
        if (o.x > canvas.width  + o.r) o.x = -o.r;
        if (o.y < -o.r) o.y = canvas.height + o.r;
        if (o.y > canvas.height + o.r) o.y = -o.r;

        const g = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, o.r);
        g.addColorStop(0, o.color + opacity);
        g.addColorStop(1, o.color + '00');
        ctx.beginPath();
        ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return <canvas ref={canvasRef} className="auth-canvas" />;
};

// ── Feature row ──────────────────────────────────────────────────────────────
const Feature = ({ icon, title, desc }) => (
  <Box className="auth-feature">
    <Box className="auth-feature__icon">{icon}</Box>
    <Box>
      <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#ffffff', lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.25, lineHeight: 1.5 }}>
        {desc}
      </Typography>
    </Box>
  </Box>
);

// ── Component ────────────────────────────────────────────────────────────────
const AuthPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);

  const handleGoogleSuccess = (response) => {
    trackSignupSuccess();
    dispatch(GoogleSignIn(response.credential, navigate));
  };

  return (
    <>
      <Box className="auth-root">
      <OrbCanvas mode={mode} />

      <Box className="auth-content">

        {/* ── Left: marketing panel ─────────────────────────────────────── */}
        <Box className="auth-left">

          <Box mb={4}>
            <LogoSVG height="34px" />
          </Box>

          <Chip
            icon={<AutoAwesomeRoundedIcon sx={{ fontSize: '12px !important' }} />}
            label="Built for focused minds"
            size="small"
            className="auth-chip"
          />

          <Typography className="auth-headline">
            Think clearly.<br />Write freely.<br />Remember everything.
          </Typography>

          <Typography className="auth-subline">
            NoteCORE is the intelligent writing workspace that keeps your
            ideas organised, searchable, and always with you.
          </Typography>

          <Box className="auth-features">
            <Feature
              icon={<FlashOnRoundedIcon sx={{ fontSize: 18 }} />}
              title="Instant richtext editor"
              desc="Blocks, headings, lists — all without leaving the keyboard."
            />
            <Feature
              icon={<SyncRoundedIcon sx={{ fontSize: 18 }} />}
              title="Syncs across every device"
              desc="Open on your phone, pick up on your laptop. Always in sync."
            />
            <Feature
              icon={<LockOutlinedIcon sx={{ fontSize: 18 }} />}
              title="Private by default"
              desc="Your notes are yours. Zero ads, zero tracking."
            />
          </Box>
{/* 
          <Box className="auth-testimonial">
            <FormatQuoteRoundedIcon sx={{ fontSize: 20, opacity: 0.5, mb: 0.5 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontStyle: 'italic', lineHeight: 1.65 }}>
              "NoteCORE changed how I capture and act on ideas. It's the
              cleanest writing tool I've ever used."
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', mt: 1, display: 'block' }}>
              — Early beta user
            </Typography>
          </Box> */}

        </Box>

        {/* ── Right: login card ─────────────────────────────────────────── */}
        <Box className="auth-right">
          <Box className="auth-card">

            {/* mobile-only logo */}
            <Box className="auth-card__logo-mobile" mb={2.5}>
              <LogoSVG height="28px" />
            </Box>

            <Chip
              icon={<AutoAwesomeRoundedIcon sx={{ fontSize: '12px !important', color: '#c4b5fd !important' }} />}
              label="Free to get started"
              size="small"
              sx={{
                mb: 2.5,
                backgroundColor: 'rgba(139,92,246,0.22)',
                color: '#e9d5ff',
                fontWeight: 600,
                fontSize: '11px',
                border: '1px solid rgba(167,139,250,0.45)',
                alignSelf: 'flex-start',
              }}
            />

            <Typography variant="h5" fontWeight={700} mb={0.5} sx={{ color: '#f0f0f0' }}>
              Welcome back
            </Typography>

            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.62)', mb: 3.5, lineHeight: 1.6 }}>
              Sign in to continue to your workspace.
            </Typography>

            {/* ── Google sign-in button ── */}
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => {}}
              useOneTap={false}
              width="316"
            />

            <Box className="auth-card__divider">
              <span />
            </Box>

            <Typography
              component="p"
              sx={{ color: 'rgba(255,255,255,0.35)', textAlign: 'center', lineHeight: 1.5, display: 'block', fontSize: '11px' }}
            >
              By signing in, you agree to our{' '}
              <RouterLink
                to="/terms"
                style={{ color: 'rgba(167,139,250,0.75)', textDecoration: 'none', fontWeight: 600 }}
                onMouseEnter={e => e.target.style.color = '#c4b5fd'}
                onMouseLeave={e => e.target.style.color = 'rgba(167,139,250,0.75)'}
              >
                Terms of Service
              </RouterLink>
              {' '}&amp;{' '}
              <RouterLink
                to="/privacy"
                style={{ color: 'rgba(167,139,250,0.75)', textDecoration: 'none', fontWeight: 600 }}
                onMouseEnter={e => e.target.style.color = '#c4b5fd'}
                onMouseLeave={e => e.target.style.color = 'rgba(167,139,250,0.75)'}
              >
                Privacy Policy
              </RouterLink>.
            </Typography>

          </Box>
        </Box>
      </Box>
      </Box>
    </>
  );
};

export default AuthPage;
